// Automated Terraform (HCL) Generator for AWS Infrastructure
// Produces modular, production-grade Infrastructure as Code

export const generateTerraformHcl = (config) => {
  const {
    region = 'us-east-1',
    ec2 = {},
    rds = {},
    networking = {}
  } = config;

  const instanceType = ec2.instanceId || 't4g.small';
  const instanceQty = Math.max(1, Number(ec2.quantity) || 2);
  const ebsSize = Math.max(10, Number(ec2.ebsSizeGb) || 30);
  const rdsEngine = rds.engine || 'postgres';
  const rdsClass = rds.instanceClass || 'db.t4g.micro';
  const rdsMultiAz = Boolean(rds.multiAz);
  const rdsStorage = Math.max(20, Number(rds.storageGb) || 20);
  const natCount = Math.max(0, Number(networking.natGatewayCount) || 1);

  return `# ==============================================================================
# AWS Multi-Tier Infrastructure Architecture
# Generated by Adedeji Babalola's AWS & Multi-Cloud Infrastructure Estimator
# Region: ${region}
# ==============================================================================

terraform {
  required_version = ">= 1.5.0"
  required_providers {
    aws = {
      source  = "hashicorp/aws"
      version = "~> 5.30.0"
    }
  }
}

provider "aws" {
  region = "${region}"

  default_tags {
    tags = {
      Environment = "Production"
      ManagedBy   = "Terraform"
      Project     = "CloudArchitectureDemo"
      Architect   = "Adedeji-Babalola"
    }
  }
}

# ------------------------------------------------------------------------------
# 1. Virtual Private Cloud (VPC) & Multi-AZ Subnets
# ------------------------------------------------------------------------------

resource "aws_vpc" "main" {
  cidr_block           = "10.0.0.0/16"
  enable_dns_hostnames = true
  enable_dns_support   = true

  tags = {
    Name = "app-production-vpc"
  }
}

# Internet Gateway
resource "aws_internet_gateway" "igw" {
  vpc_id = aws_vpc.main.id

  tags = {
    Name = "app-production-igw"
  }
}

# Public Subnets (ALB & NAT Gateways)
resource "aws_subnet" "public_1" {
  vpc_id                  = aws_vpc.main.id
  cidr_block              = "10.0.1.0/24"
  availability_zone       = "${region}a"
  map_public_ip_on_launch = true

  tags = { Name = "public-subnet-1a" }
}

resource "aws_subnet" "public_2" {
  vpc_id                  = aws_vpc.main.id
  cidr_block              = "10.0.2.0/24"
  availability_zone       = "${region}b"
  map_public_ip_on_launch = true

  tags = { Name = "public-subnet-1b" }
}

# Private App Subnets (EC2 Compute Instances)
resource "aws_subnet" "private_app_1" {
  vpc_id            = aws_vpc.main.id
  cidr_block        = "10.0.10.0/24"
  availability_zone = "${region}a"

  tags = { Name = "private-app-subnet-1a" }
}

resource "aws_subnet" "private_app_2" {
  vpc_id            = aws_vpc.main.id
  cidr_block        = "10.0.20.0/24"
  availability_zone = "${region}b"

  tags = { Name = "private-app-subnet-1b" }
}

# Isolated Database Subnets (Amazon RDS)
resource "aws_subnet" "db_subnet_1" {
  vpc_id            = aws_vpc.main.id
  cidr_block        = "10.0.30.0/24"
  availability_zone = "${region}a"

  tags = { Name = "private-db-subnet-1a" }
}

resource "aws_subnet" "db_subnet_2" {
  vpc_id            = aws_vpc.main.id
  cidr_block        = "10.0.40.0/24"
  availability_zone = "${region}b"

  tags = { Name = "private-db-subnet-1b" }
}

${natCount > 0 ? `# Elastic IP & NAT Gateway for Outbound Private Routing
resource "aws_eip" "nat_eip" {
  domain = "vpc"
  tags   = { Name = "app-nat-eip" }
}

resource "aws_nat_gateway" "nat_gw" {
  allocation_id = aws_eip.nat_eip.id
  subnet_id     = aws_subnet.public_1.id

  tags = { Name = "app-nat-gateway" }
  depends_on = [aws_internet_gateway.igw]
}

resource "aws_route_table" "private_app" {
  vpc_id = aws_vpc.main.id

  route {
    cidr_block     = "0.0.0.0/0"
    nat_gateway_id = aws_nat_gateway.nat_gw.id
  }

  tags = { Name = "private-app-rt" }
}

resource "aws_route_table_association" "app_1" {
  subnet_id      = aws_subnet.private_app_1.id
  route_table_id = aws_route_table.private_app.id
}

resource "aws_route_table_association" "app_2" {
  subnet_id      = aws_subnet.private_app_2.id
  route_table_id = aws_route_table.private_app.id
}` : '# No NAT Gateway configured in this architecture tier.'}

# ------------------------------------------------------------------------------
# 2. Security Groups (Least Privilege Isolation)
# ------------------------------------------------------------------------------

# ALB Security Group: Inbound HTTPS/HTTP from Internet
resource "aws_security_group" "alb" {
  name        = "app-alb-sg"
  description = "Allow inbound HTTP/HTTPS from Internet"
  vpc_id      = aws_vpc.main.id

  ingress {
    description = "HTTP Inbound"
    from_port   = 80
    to_port     = 80
    protocol    = "tcp"
    cidr_blocks = ["0.0.0.0/0"]
  }

  ingress {
    description = "HTTPS Inbound"
    from_port   = 443
    to_port     = 443
    protocol    = "tcp"
    cidr_blocks = ["0.0.0.0/0"]
  }

  egress {
    from_port   = 0
    to_port     = 0
    protocol    = "-1"
    cidr_blocks = ["0.0.0.0/0"]
  }

  tags = { Name = "alb-security-group" }
}

# EC2 App Security Group: Inbound from ALB only
resource "aws_security_group" "app" {
  name        = "app-ec2-sg"
  description = "Allow ingress strictly from Application Load Balancer"
  vpc_id      = aws_vpc.main.id

  ingress {
    description     = "Traffic from ALB"
    from_port       = 8080
    to_port         = 8080
    protocol        = "tcp"
    security_groups = [aws_security_group.alb.id]
  }

  egress {
    from_port   = 0
    to_port     = 0
    protocol    = "-1"
    cidr_blocks = ["0.0.0.0/0"]
  }

  tags = { Name = "ec2-app-security-group" }
}

# RDS DB Security Group: Inbound strictly from EC2 App SG
resource "aws_security_group" "db" {
  name        = "app-rds-db-sg"
  description = "Allow database ingress strictly from App Tier"
  vpc_id      = aws_vpc.main.id

  ingress {
    description     = "Database connection from App instances"
    from_port       = ${rdsEngine === 'mysql' ? 3306 : 5432}
    to_port         = ${rdsEngine === 'mysql' ? 3306 : 5432}
    protocol        = "tcp"
    security_groups = [aws_security_group.app.id]
  }

  egress {
    from_port   = 0
    to_port     = 0
    protocol    = "-1"
    cidr_blocks = ["0.0.0.0/0"]
  }

  tags = { Name = "rds-db-security-group" }
}

# ------------------------------------------------------------------------------
# 3. Application Load Balancer (ALB)
# ------------------------------------------------------------------------------

resource "aws_lb" "external" {
  name               = "production-alb"
  internal           = false
  load_balancer_type = "application"
  security_groups    = [aws_security_group.alb.id]
  subnets            = [aws_subnet.public_1.id, aws_subnet.public_2.id]

  enable_deletion_protection = false

  tags = { Name = "production-alb" }
}

resource "aws_lb_target_group" "app_tg" {
  name     = "production-app-tg"
  port     = 8080
  protocol = "HTTP"
  vpc_id   = aws_vpc.main.id

  health_check {
    path                = "/health"
    healthy_threshold   = 3
    unhealthy_threshold = 2
    timeout             = 5
    interval            = 30
    matcher             = "200"
  }
}

resource "aws_lb_listener" "http" {
  load_balancer_arn = aws_lb.external.arn
  port              = 80
  protocol          = "HTTP"

  default_action {
    type             = "forward"
    target_group_arn = aws_lb_target_group.app_tg.arn
  }
}

# ------------------------------------------------------------------------------
# 4. Compute: EC2 Launch Template & Auto Scaling Group
# ------------------------------------------------------------------------------

resource "aws_launch_template" "app_lt" {
  name_prefix   = "app-launch-template-"
  image_id      = "ami-0c7217cdde317cfec" # Amazon Linux 2023 AMI
  instance_type = "${instanceType}"

  vpc_security_group_ids = [aws_security_group.app.id]

  block_device_mappings {
    device_name = "/dev/xvda"

    ebs {
      volume_size           = ${ebsSize}
      volume_type           = "${ec2.ebsType || 'gp3'}"
      encrypted             = true
      delete_on_termination = true
    }
  }

  tag_specifications {
    resource_type = "instance"
    tags = {
      Name = "production-app-instance"
    }
  }
}

resource "aws_autoscaling_group" "app_asg" {
  name                = "production-app-asg"
  vpc_zone_identifier = [aws_subnet.private_app_1.id, aws_subnet.private_app_2.id]
  target_group_arns   = [aws_lb_target_group.app_tg.arn]

  min_size         = ${Math.max(1, Math.floor(instanceQty / 2))}
  max_size         = ${instanceQty * 2}
  desired_capacity = ${instanceQty}

  launch_template {
    id      = aws_launch_template.app_lt.id
    version = "$Latest"
  }

  health_check_type         = "ELB"
  health_check_grace_period = 300

  tag {
    key                 = "Name"
    value               = "app-asg-worker"
    propagate_at_launch = true
  }
}

# ------------------------------------------------------------------------------
# 5. Database: Amazon RDS (${rdsEngine.toUpperCase()})
# ------------------------------------------------------------------------------

resource "aws_db_subnet_group" "db_subnet_grp" {
  name       = "app-db-subnet-group"
  subnet_ids = [aws_subnet.db_subnet_1.id, aws_subnet.db_subnet_2.id]

  tags = { Name = "app-db-subnet-group" }
}

resource "aws_db_instance" "database" {
  identifier           = "app-production-db"
  engine               = "${rdsEngine === 'mysql' ? 'mysql' : 'postgres'}"
  instance_class       = "${rdsClass}"
  allocated_storage    = ${rdsStorage}
  max_allocated_storage = ${rdsStorage * 3}
  storage_type         = "gp3"
  multi_az             = ${rdsMultiAz ? 'true' : 'false'}
  
  db_name  = "app_production"
  username = "dbadmin"
  password = "CHANGE_ME_SECURE_PASSWORD" # Store in AWS Secrets Manager

  db_subnet_group_name   = aws_db_subnet_group.db_subnet_grp.name
  vpc_security_group_ids = [aws_security_group.db.id]
  skip_final_snapshot    = true
  storage_encrypted      = true

  backup_retention_period = ${rds.backupRetentionDays || 7}

  tags = { Name = "app-production-db" }
}

# ------------------------------------------------------------------------------
# 6. Storage: Secure Amazon S3 Object Bucket
# ------------------------------------------------------------------------------

resource "aws_s3_bucket" "assets" {
  bucket_prefix = "app-production-assets-"

  tags = {
    Name        = "app-assets-bucket"
    Environment = "Production"
  }
}

# Enforce Server-Side Encryption
resource "aws_s3_bucket_server_side_encryption_configuration" "assets_crypto" {
  bucket = aws_s3_bucket.assets.id

  rule {
    apply_server_side_encryption_by_default {
      sse_algorithm = "AES256"
    }
  }
}

# Block all Public Access
resource "aws_s3_bucket_public_access_block" "assets_access" {
  bucket                  = aws_s3_bucket.assets.id
  block_public_acls       = true
  block_public_policy     = true
  ignore_public_acls      = true
  restrict_public_buckets = true
}

# ------------------------------------------------------------------------------
# Outputs
# ------------------------------------------------------------------------------

output "alb_dns_name" {
  description = "The public DNS endpoint of the Application Load Balancer"
  value       = aws_lb.external.dns_name
}

output "rds_endpoint" {
  description = "The connection endpoint for the Amazon RDS database"
  value       = aws_db_instance.database.endpoint
}

output "s3_bucket_arn" {
  description = "ARN of the provisioned secure S3 assets bucket"
  value       = aws_s3_bucket.assets.arn
}
`;
};
